import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";

/**
 * Workshop: Settings modal shell — backdrop, grouped sidebar nav, content area.
 *
 * Pure presentational component. The parent provides a `renderContent` callback
 * that decides what to render for each tab. This keeps the modal shell reusable
 * and decoupled from individual section implementations.
 */

/* ---------- Types ---------- */

export type SettingsTab =
  | "profile"
  | "billing"
  | "connected-accounts"
  | "companion"
  | "agents"
  | "projects"
  | "appearance"
  | "notifications"
  | "contact"
  | "help";

export interface SettingsModalProps {
  open: boolean;
  activeTab: SettingsTab;
  onTabChange: (tab: SettingsTab) => void;
  onClose: () => void;
  renderContent: (tab: SettingsTab) => ReactNode;
}

/* ---------- Nav config ---------- */

interface NavItem {
  tab: SettingsTab;
  label: string;
  icon: React.ReactNode;
  testId: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Account",
    items: [
      {
        tab: "profile",
        label: "Profile",
        icon: <Icons.User className="h-4 w-4" />,
        testId: "settings-nav-profile",
      },
      {
        tab: "billing",
        label: "Billing",
        icon: <Icons.CreditCard className="h-4 w-4" />,
        testId: "settings-nav-billing",
      },
      {
        tab: "connected-accounts",
        label: "Connected Accounts",
        icon: <Icons.Plug className="h-4 w-4" />,
        testId: "settings-nav-connected-accounts",
      },
      {
        tab: "companion",
        label: "Companion",
        icon: <Icons.Terminal className="h-4 w-4" />,
        testId: "settings-nav-companion",
      },
    ],
  },
  {
    label: "Workspace",
    items: [
      {
        tab: "agents",
        label: "Agents",
        icon: <Icons.Bot className="h-4 w-4" />,
        testId: "settings-nav-agents",
      },
      {
        tab: "projects",
        label: "Projects",
        icon: <Icons.FolderTree className="h-4 w-4" />,
        testId: "settings-nav-projects",
      },
      {
        tab: "appearance",
        label: "Appearance",
        icon: <Icons.Palette className="h-4 w-4" />,
        testId: "settings-nav-appearance",
      },
      {
        tab: "notifications",
        label: "Notifications",
        icon: <Icons.Bell className="h-4 w-4" />,
        testId: "settings-nav-notifications",
      },
    ],
  },
  {
    label: "Support",
    items: [
      {
        tab: "contact",
        label: "Contact",
        icon: <Icons.Mail className="h-4 w-4" />,
        testId: "settings-nav-contact",
      },
      {
        tab: "help",
        label: "Help",
        icon: <Icons.HelpCircle className="h-4 w-4" />,
        testId: "settings-nav-help",
      },
    ],
  },
];

/** Human-readable title for each tab. */
const TAB_TITLES: Record<SettingsTab, string> = {
  profile: "Profile",
  billing: "Billing",
  "connected-accounts": "Connected Accounts",
  companion: "Companion",
  agents: "Agents",
  projects: "Projects",
  appearance: "Appearance",
  notifications: "Notifications",
  contact: "Contact",
  help: "Help",
};

/* ---------- Component ---------- */

export function SettingsModal({
  open,
  activeTab,
  onTabChange,
  onClose,
  renderContent,
}: SettingsModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  /* Close on Escape */
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  /* Close on backdrop click */
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={handleBackdropClick}
      data-testid="settings-modal-backdrop"
    >
      <div
        ref={containerRef}
        className="flex h-[600px] w-[800px] max-h-[85vh] overflow-hidden rounded-glass border border-glass-stroke-light bg-glass-fill-heavy shadow-glass backdrop-blur-glass-sm"
        data-testid="settings-modal"
      >
        {/* ---- Left sidebar ---- */}
        <nav
          className="flex w-[200px] shrink-0 flex-col gap-4 border-r border-glass-stroke-light p-4"
          data-testid="settings-sidebar"
        >
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="flex flex-col gap-0.5">
              <span className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-secondary">
                {group.label}
              </span>
              {group.items.map((item) => {
                const isActive = item.tab === activeTab;
                return (
                  <button
                    key={item.tab}
                    type="button"
                    onClick={() => onTabChange(item.tab)}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors",
                      isActive
                        ? "bg-accent/10 font-medium text-accent"
                        : "text-secondary hover:bg-btn-ghost-fill hover:text-primary",
                    )}
                    data-testid={item.testId}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ---- Right content area ---- */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Header */}
          <div className="flex h-[48px] shrink-0 items-center justify-between border-b border-glass-stroke-light px-5">
            <h2 className="text-sm font-semibold text-primary">
              {TAB_TITLES[activeTab]}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              data-testid="settings-modal-close"
              aria-label="Close settings"
            >
              <Icons.X className="h-4 w-4" />
            </Button>
          </div>

          {/* Scrollable content */}
          <div
            className="flex-1 overflow-y-auto p-5"
            data-testid="settings-content"
          >
            {renderContent(activeTab)}
          </div>
        </div>
      </div>
    </div>
  );
}
